import {
    AppstoreOutlined,
    ArrowRightOutlined,
    BulbTwoTone,
    CheckCircleOutlined,
    CloseCircleOutlined,
    EditOutlined,
    SearchOutlined,
    ThunderboltOutlined,
    UndoOutlined,
    UnorderedListOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import { Button, Card, Checkbox, Empty, Input, Radio, Select, Space, Spin, Tag, Tooltip, Typography } from "antd";
import type React from "react";
import { useEffect, useState } from "react";
import type { BusinessArea, Tag as TagType } from "types/components/IAITagGenerationWizard";
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface ReviewStepProps {
    generatedTags: TagType[];
    selectedBusinessArea: string;
    businessAreaList: BusinessArea[];
    searchText: string;
    viewMode: "list" | "grid";
    handleBusinessAreaChange: (value: string) => void;
    handleTagSelection: (index: number) => void;
    handleSelectAll: () => void;
    handleDeselectAll: () => void;
    setSearchText: (text: string) => void;
    setViewMode: (mode: "list" | "grid") => void;
    handleRephraseDescription?: (index: number) => Promise<void>;
    handleRevertDescription?: (index: number) => void;
    handleUpdateDescription?: (index: number, description: string) => void;
    isRephrasing?: boolean;
    currentRephrasingTagIndex?: number | null;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
    generatedTags,
    selectedBusinessArea,
    businessAreaList,
    searchText,
    viewMode,
    handleBusinessAreaChange,
    handleTagSelection,
    handleSelectAll,
    handleDeselectAll,
    setSearchText,
    setViewMode,
    handleRephraseDescription,
    handleRevertDescription,
    handleUpdateDescription,
    isRephrasing,
    currentRephrasingTagIndex,
}) => {
    const [showFullDescriptionIndex, setShowFullDescriptionIndex] = useState<number | null>(null);
    const [editingDescriptionIndex, setEditingDescriptionIndex] = useState<number | null>(null);
    const [editedDescription, setEditedDescription] = useState<string>("");

    useEffect(() => {
        setShowFullDescriptionIndex(null);
    }, [searchText]);

    const filteredTags = generatedTags.filter(
        (tag) => tag.name.toLowerCase().includes(searchText.toLowerCase()) || tag.description.toLowerCase().includes(searchText.toLowerCase()),
    );
    const getOriginalIndex = (filteredIndex: number) => {
        if (filteredIndex < 0 || filteredIndex >= filteredTags.length) return -1;
        const targetTag = filteredTags[filteredIndex];
        return generatedTags.findIndex((tag) => tag.name === targetTag.name);
    };

    const startEditing = (filteredIndex: number) => {
        const originalIndex = getOriginalIndex(filteredIndex);
        if (originalIndex >= 0) {
            setEditingDescriptionIndex(filteredIndex);
            setEditedDescription(generatedTags[originalIndex].description);
        }
    };

    const saveDescription = () => {
        if (editingDescriptionIndex !== null) {
            const originalIndex = getOriginalIndex(editingDescriptionIndex);
            if (originalIndex >= 0 && handleUpdateDescription) {
                handleUpdateDescription(originalIndex, editedDescription);
            }
            setEditingDescriptionIndex(null);
            setEditedDescription("");
        }
    };

    const cancelEditing = () => {
        setEditingDescriptionIndex(null);
        setEditedDescription("");
    };
    const isTagRephrasing = (originalIndex: number) => {
        return isRephrasing && currentRephrasingTagIndex === originalIndex;
    };
    const renderDescription = (tag: TagType, filteredIndex: number) => {
        const originalIndex = getOriginalIndex(filteredIndex);
        const isEditing = editingDescriptionIndex === filteredIndex;
        const isCurrentlyRephrasing = isTagRephrasing(originalIndex);
        const descriptionLengthThreshold = 250;
        if (isEditing) {
            return (
                <div className="relative mt-2 rounded-md border border-blue-200 bg-blue-50 p-2">
                    <div className="absolute right-2 top-2 mb-3 flex gap-1">
                        <Button
                            size="small"
                            type="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                saveDescription();
                            }}
                            className="flex items-center"
                            title="Save changes"
                        >
                            <CheckCircleOutlined />
                        </Button>
                        <Button
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                cancelEditing();
                            }}
                            className="flex items-center"
                            title="Cancel editing"
                        >
                            <CloseCircleOutlined />
                        </Button>
                    </div>
                    <TextArea
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        autoSize={{ minRows: 2, maxRows: 6 }}
                        className="mb-2 mt-8"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()} 
                        autoFocus
                    />
                </div>
            );
        }

        if (isCurrentlyRephrasing) {
            return (
                <div className="mt-1 flex items-center gap-2 py-2">
                    <Spin size="small" />
                    <Text type="secondary" italic>
                        Rephrasing description with AI...
                    </Text>
                </div>
            );
        }

        const showFullDescription = showFullDescriptionIndex === filteredIndex;
        const isDescriptionLong = tag.description.length > descriptionLengthThreshold;

        return (
            <div className="relative">
                <div className="my absolute right-0 top-0">
                    <Tooltip title="Edit description">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            className="text-gray-400 hover:text-blue-500"
                            onClick={(e) => {
                                e.stopPropagation();
                                startEditing(filteredIndex);
                            }}
                            disabled={isRephrasing}
                        />
                    </Tooltip>
                </div>

                {showFullDescription ? (
                    <div className="mt-1 pr-6">
                        <Paragraph className="m-0 whitespace-pre-wrap text-sm text-gray-600">{tag.description}</Paragraph>
                        <div className="mt-2 flex flex-wrap gap-2">
                            <Tooltip title="Show less text">
                                <Button
                                    type="link"
                                    size="small"
                                    className="h-auto p-0 text-xs"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowFullDescriptionIndex(null);
                                    }}
                                >
                                    Show less
                                </Button>
                            </Tooltip>

                            {handleRephraseDescription && (
                                <Tooltip title="Use AI to improve this description">
                                    <Button
                                        type="link"
                                        size="middle"
                                        icon={<ThunderboltOutlined />}
                                        className="h-auto p-0 text-xs font-extrabold text-blue-500"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRephraseDescription(originalIndex);
                                        }}
                                        disabled={isRephrasing}
                                    >
                                        {isRephrasing ? "Rephrasing..." : "Rephrase with AI"}
                                    </Button>
                                </Tooltip>
                            )}

                            {tag.originalDescription && handleRevertDescription && (
                                <Tooltip title="Restore original description">
                                    <Button
                                        type="link"
                                        size="small"
                                        danger
                                        icon={<UndoOutlined />}
                                        className="h-auto p-0 text-xs font-bold"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRevertDescription(originalIndex);
                                        }}
                                    >
                                        Revert
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="mt-1 pr-6">
                        <Paragraph className="m-0 text-sm text-gray-600" ellipsis={isDescriptionLong ? { rows: 2 } : false}>
                            {tag.description}
                        </Paragraph>
                        <div className="mt-1 flex flex-wrap gap-2">
                            {isDescriptionLong && (
                                <Tooltip title="Show full description">
                                    <Button
                                        type="link"
                                        size="small"
                                        className="h-auto p-0 text-xs"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowFullDescriptionIndex(filteredIndex);
                                        }}
                                    >
                                        Show more
                                    </Button>
                                </Tooltip>
                            )}
                            {handleRephraseDescription && (
                                <Tooltip title="Use AI to improve this description">
                                    <Button
                                        type="link"
                                        size="middle"
                                        icon={<ThunderboltOutlined />}
                                        className="h-auto p-0 text-xs font-extrabold text-blue-500"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRephraseDescription(originalIndex);
                                        }}
                                        disabled={isRephrasing}
                                    >
                                        {isRephrasing ? "Rephrasing..." : "Rephrase with AI"}
                                    </Button>
                                </Tooltip>
                            )}
                            {tag.originalDescription && handleRevertDescription && (
                                <Tooltip title="Restore original description">
                                    <Button
                                        type="link"
                                        size="small"
                                        danger
                                        icon={<UndoOutlined />}
                                        className="h-auto p-0 text-xs font-bold"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRevertDescription(originalIndex);
                                        }}
                                    >
                                        Revert
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="mt-4 space-y-5">
            <div className="mb-3 flex items-center justify-between">
                <Title level={4}>Review Generated Tags</Title>
                <Tooltip
                    title={
                        <div>
                            <Text strong className="font-semibold text-blue-800">
                                Pro Tip
                            </Text>
                            <Paragraph className="m-0 text-sm text-blue-500">
                                Select only the most relevant tags for your use case. You can always generate more tags later. Use the search to quickly find specific tags. The
                                business area you select will be applied to all tags.
                            </Paragraph>
                        </div>
                    }
                    placement="topLeft"
                    styles={{
                        container: {
                            backgroundColor: "white",
                            borderRadius: "8px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                        },
                    }}
                >
                    <div className="flex cursor-pointer items-center gap-2">
                        <BulbTwoTone className="text-blue-500" style={{ fontSize: "24px" }} />
                    </div>
                </Tooltip>
            </div>
            <div className="mb-3 grid grid-cols-2 gap-4">
                <div>
                    <Text strong>Business Area</Text>
                    <Select
                            showSearch={{
                            filterOption:(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }}
                            className="w-full mt-2"
                            value={selectedBusinessArea}
                            onChange={handleBusinessAreaChange}
                            placeholder="Select business area"
                            options={businessAreaList.map((area) => ({
                                    value: area.businessAreaId,
                                    label: area.name,
                            }))}
                        />
                    <Text type="secondary" className="mt-1 block text-xs">
                        You can change the business area for these tags
                    </Text>
                </div>
                <div>
                    <Text strong>Search Tags</Text>
                    <Input
                        prefix={<SearchOutlined className="text-gray-400" />}
                        placeholder="Search by name or description"
                        className="mt-2 w-full"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                        aria-label="Search tags"
                    />
                    <div className="mt-1 flex justify-between">
                        <Text type="secondary" className="text-xs">
                            {filteredTags.length} of {generatedTags.length} tags shown
                        </Text>
                        <div>
                            <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)} size="small" buttonStyle="solid" aria-label="Change view mode">
                                <Radio.Button value="list" aria-label="List view">
                                    <UnorderedListOutlined />
                                </Radio.Button>
                                <Radio.Button value="grid" aria-label="Grid view">
                                    <AppstoreOutlined />
                                </Radio.Button>
                            </Radio.Group>
                        </div>
                    </div>
                </div>
            </div>

            <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
                <div className="flex items-center justify-between border-b bg-blue-50 p-3">
                    <Text strong>
                        <CheckCircleOutlined className="mr-1 text-green-500" />
                        {generatedTags.filter((t) => t.selected).length} of {generatedTags.length} tags selected
                    </Text>
                    <Space size="middle">
                        {generatedTags.some((tag) => !tag.selected) && (
                            <Tooltip title="Select all tags">
                                <Button shape="circle" icon={<CheckCircleOutlined />} onClick={handleSelectAll} type="primary" ghost size="small" />
                            </Tooltip>
                        )}
                        {generatedTags.some((tag) => tag.selected) && (
                            <Tooltip title="Deselect all tags">
                                <Button shape="circle" icon={<CloseCircleOutlined />} onClick={handleDeselectAll} danger ghost size="small" />
                            </Tooltip>
                        )}
                    </Space>
                </div>

                {filteredTags.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tags match your search criteria" className="py-10" />
                ) : viewMode === "list" ? (
                    <div className="max-h-[320px] overflow-y-auto">
                        {filteredTags.map((tag, filteredIndex) => {
                            const originalIndex = getOriginalIndex(filteredIndex);
                            const isCurrentlyRephrasing = isTagRephrasing(originalIndex);
                            return (
                                <div
                                    key={tag.name}
                                    className={`cursor-pointer border-b p-4 transition-colors last:border-b-0 hover:bg-gray-50 ${tag.selected ? "border-l-4 border-l-blue-500 pl-3" : "border-l-4 border-l-transparent pl-4"} ${isCurrentlyRephrasing ? "bg-blue-50" : ""} `}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleTagSelection(originalIndex);
                                    }}
                                    role="checkbox"
                                    aria-checked={tag.selected}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
                                            e.preventDefault();
                                            handleTagSelection(originalIndex);
                                        }
                                    }}
                                >
                                    <div className="flex items-start gap-3">
                                        <Checkbox
                                            checked={tag.selected}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleTagSelection(originalIndex);
                                            }}
                                            className="mt-1 flex-shrink-0"
                                        />
                                        <div className="flex-grow">
                                            <div className="mb-2 flex items-center gap-2">
                                                <Tag color={tag.isDuplicate ? "orange" : "blue"} className="px-2 py-1 text-sm font-medium">
                                                    {tag.name}
                                                </Tag>
                                                {tag.isDuplicate && (
                                                    <Tooltip
                                                        title={
                                                            <div>
                                                                <div className="mb-1">Renamed to avoid duplicate with existing Tag</div>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-gray-400 line-through">{tag.originalName}</span>
                                                                    <ArrowRightOutlined className="text-xs" />
                                                                    <span className="font-medium">{tag.name}</span>
                                                                </div>
                                                            </div>
                                                        }
                                                    >
                                                        <Tag className="m-0 flex items-center border-orange-200 bg-orange-50 px-1.5 text-orange-600">
                                                            <WarningOutlined className="mr-1 text-sm" />
                                                            <span className="text-xs font-semibold">Renamed</span>
                                                        </Tag>
                                                    </Tooltip>
                                                )}
                                            </div>
                                            {renderDescription(tag, filteredIndex)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="grid max-h-[320px] grid-cols-2 gap-2 overflow-y-auto p-3 md:grid-cols-3">
                        {filteredTags.map((tag, filteredIndex) => {
                            const originalIndex = getOriginalIndex(filteredIndex);
                            const isCurrentlyRephrasing = isTagRephrasing(originalIndex);
                            return (
                                <Card
                                    key={tag.name}
                                    size="small"
                                    hoverable
                                    className={`h-full cursor-pointer overflow-hidden transition-shadow hover:shadow-md ${tag.selected ? "border-blue-500" : "border-gray-200"} ${isCurrentlyRephrasing ? "bg-blue-50" : ""} `}
                                    onClick={() => handleTagSelection(originalIndex)}
                                    role="checkbox"
                                    aria-checked={tag.selected}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            handleTagSelection(originalIndex);
                                        }
                                    }}
                                >
                                    <div className="flex items-start">
                                        <Checkbox
                                            checked={tag.selected}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleTagSelection(originalIndex);
                                            }}
                                            className="mr-2 mt-1 flex-shrink-0"
                                        />
                                        <div className="w-full">
                                            <div className="mb-2 flex items-start justify-between">
                                                <div className="flex max-w-[85%] flex-wrap items-center gap-1">
                                                    <Tag color={tag.isDuplicate ? "orange" : "blue"} className="py-1 text-xs">
                                                        {tag.name}
                                                    </Tag>
                                                    {tag.isDuplicate && (
                                                        <Tooltip
                                                            title={
                                                                <div>
                                                                    <div className="mb-1">Renamed to avoid duplicate</div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-gray-400 line-through">{tag.originalName}</span>
                                                                        <ArrowRightOutlined className="text-xs" />
                                                                        <span className="font-medium">{tag.name}</span>
                                                                    </div>
                                                                </div>
                                                            }
                                                        >
                                                            <Tag className="m-0 flex items-center border-orange-200 bg-orange-50 px-1 text-orange-600">
                                                                <WarningOutlined className="mr-0.5 text-xs" />
                                                                <span className="text-xs">Renamed</span>
                                                            </Tag>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                                <Tooltip title="Edit description">
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<EditOutlined />}
                                                        className="-mr-1 -mt-1 text-gray-400 hover:text-blue-500"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            startEditing(filteredIndex);
                                                        }}
                                                        disabled={isRephrasing}
                                                    />
                                                </Tooltip>
                                            </div>

                                            {editingDescriptionIndex === filteredIndex ? (
                                                <div className="relative rounded-md border border-blue-200 bg-blue-50 p-2">
                                                    <TextArea
                                                        value={editedDescription}
                                                        onChange={(e) => setEditedDescription(e.target.value)}
                                                        autoSize={{ minRows: 2, maxRows: 4 }}
                                                        className="mb-2"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onKeyDown={(e) => e.stopPropagation()} 
                                                        autoFocus
                                                    />
                                                    <div className="flex justify-end gap-1">
                                                        <Button
                                                            size="small"
                                                            type="primary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                saveDescription();
                                                            }}
                                                            className="flex items-center"
                                                            title="Save changes"
                                                        >
                                                            <CheckCircleOutlined />
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                cancelEditing();
                                                            }}
                                                            className="flex items-center"
                                                            title="Cancel editing"
                                                        >
                                                            <CloseCircleOutlined />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : isCurrentlyRephrasing ? (
                                                <div className="flex items-center gap-2 py-1">
                                                    <Spin size="small" />
                                                    <Text type="secondary" italic className="text-xs">
                                                        Rephrasing...
                                                    </Text>
                                                </div>
                                            ) : (
                                                <>
                                                    <Paragraph className="m-0 text-xs text-gray-600" ellipsis={{ rows: 3 }}>
                                                        {tag.description}
                                                    </Paragraph>

                                                    <div className="mt-1 flex flex-wrap gap-1">
                                                        {showFullDescriptionIndex === filteredIndex ? (
                                                            <Tooltip title="Show less text">
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    className="h-auto p-0 text-xs"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setShowFullDescriptionIndex(null);
                                                                    }}
                                                                >
                                                                    Show less
                                                                </Button>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title="Show full description">
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    className="h-auto p-0 text-xs"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setShowFullDescriptionIndex(filteredIndex);
                                                                    }}
                                                                >
                                                                    Show more
                                                                </Button>
                                                            </Tooltip>
                                                        )}

                                                        {handleRephraseDescription && (
                                                            <Tooltip title="Use AI to improve this description">
                                                                <Button
                                                                    type="link"
                                                                    size="middle"
                                                                    icon={<ThunderboltOutlined className="font-extrabold" />}
                                                                    className="h-auto p-0 text-xs font-extrabold text-blue-500"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleRephraseDescription(originalIndex);
                                                                    }}
                                                                    disabled={isRephrasing}
                                                                >
                                                                    {isRephrasing ? "Rephrasing..." : ""}
                                                                </Button>
                                                            </Tooltip>
                                                        )}

                                                        {tag.originalDescription && handleRevertDescription && (
                                                            <Tooltip title="Restore original description">
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    danger
                                                                    icon={<UndoOutlined className="font-extrabold" />}
                                                                    className="h-auto p-0 text-xs font-bold"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleRevertDescription(originalIndex);
                                                                    }}
                                                                >
                                                                    {""}
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
